import React from 'react';
import { Avatar, Button, Divider, Modal, Progress, Typography } from 'antd';
import {
  BarChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CommentOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  MailOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  RightOutlined,
  TeamOutlined
} from '@ant-design/icons';
import {
  DisciplineStatusDisplay,
  DisciplineStatusKey,
  getDisciplineStatusDisplay
} from 'services/graduate-enrollment/discipline-status';
import { IStudentShow } from 'interfaces/professor_area/students';
import { formatDate } from 'services/date';
import { getInitialsName } from 'utils/get-initials-name';
import { isClassroomForumEnabled } from 'services/tenants';
import { scoreToTen } from 'utils/score-formatter';
import { useModal, useNavigation } from '@refinedev/core';
import { useParams } from 'react-router-dom';

const { Text, Title } = Typography;

const STATUS_DESCRIPTION: Partial<Record<DisciplineStatusKey, string>> = {
  attendance_failed: 'Frequência mínima não atingida.',
  progress_failed: 'Progresso abaixo do mínimo exigido.',
  failed: 'Nota insuficiente para aprovação.',
  approved: 'Critérios de aprovação atendidos.',
  progress: 'Disciplina ainda em andamento.',
  in_progress: 'Disciplina ainda em andamento.',
  exempted: 'Dispensado da disciplina por equivalência.'
};

const STATUS_TONES: Record<
  string,
  {
    card: string;
    titleClass: string;
    iconClass: string;
    Icon: typeof CloseCircleOutlined;
  }
> = {
  error: {
    card: 'border border-red-200 bg-red-50',
    titleClass: '!text-red-800',
    iconClass: 'text-red-500',
    Icon: CloseCircleOutlined
  },
  success: {
    card: 'border border-emerald-200 bg-emerald-50',
    titleClass: '!text-emerald-800',
    iconClass: 'text-emerald-600',
    Icon: CheckCircleOutlined
  },
  warning: {
    card: 'border border-amber-200 bg-amber-50',
    titleClass: '!text-amber-800',
    iconClass: 'text-amber-500',
    Icon: ExclamationCircleOutlined
  },
  blue: {
    card: 'border border-blue-200 bg-blue-50',
    titleClass: '!text-blue-800',
    iconClass: 'text-blue-500',
    Icon: ClockCircleOutlined
  },
  default: {
    card: 'border border-neutral-200 bg-neutral-50',
    titleClass: '!text-neutral-800',
    iconClass: 'text-neutral-400',
    Icon: InfoCircleOutlined
  }
};

const SectionTitle = ({
  icon,
  children
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="mb-3 flex items-center gap-2">
    <span className="flex">{icon}</span>
    <Text strong className="!text-[15px]">
      {children}
    </Text>
  </div>
);

const LabeledValue = ({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <Text type="secondary" className="!text-xs">
      {label}
    </Text>
    <Text className="!text-neutral-900">{value ?? '-'}</Text>
  </div>
);

const CourseInfoSection = ({
  student,
  classroomCode,
  disciplineName
}: {
  student: IStudentShow;
  classroomCode?: string;
  disciplineName?: string;
}) => {
  const metrics = [
    classroomCode ? { label: 'Turma', value: classroomCode } : null,
    {
      label: 'Data de inscrição',
      value: formatDate(
        student.course_enrollment.created_at,
        'DD/MM/YYYY HH:mm'
      )
    },
    {
      label: 'Data de expiração',
      value: formatDate(
        student.course_enrollment.expires_at,
        'DD/MM/YYYY HH:mm'
      )
    }
  ].filter(Boolean) as { label: string; value: React.ReactNode }[];

  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200">
      <div className="px-3 py-2">
        <LabeledValue label="Curso" value={student.course_name} />
      </div>
      {disciplineName && (
        <>
          <Divider className="!m-0" />
          <div className="px-3 py-2">
            <LabeledValue label="Disciplina" value={disciplineName} />
          </div>
        </>
      )}
      <Divider className="!m-0" />
      <div
        className={`grid ${
          metrics.length === 3 ? 'grid-cols-3' : 'grid-cols-2'
        }`}
      >
        {metrics.map((metric, index) => (
          <div
            key={metric.label}
            className={`px-3 py-2 ${
              index > 0 ? 'border-l border-neutral-200' : ''
            }`}
          >
            <LabeledValue label={metric.label} value={metric.value} />
          </div>
        ))}
      </div>
    </div>
  );
};

const StatCard = ({
  title,
  indicator,
  extra
}: {
  title: string;
  indicator: React.ReactNode;
  extra?: React.ReactNode;
}) => (
  <div className="grid h-full grid-rows-[24px_56px_24px] justify-items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-3">
    <Text strong className="leading-6 !text-neutral-900">
      {title}
    </Text>
    <div className="flex h-14 w-full items-center justify-center">
      {indicator}
    </div>
    <div className="flex h-6 w-full items-center justify-center">{extra}</div>
  </div>
);

const formatScoreValue = (value: number | null | undefined) => {
  const ten = scoreToTen(value);

  if (ten === null) {
    return '-';
  }

  return ten.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
};

const ScoreCard = ({ student }: { student: IStudentShow }) => {
  const hasFinalScore = student.score !== null && student.score !== undefined;
  const hasPartialScore =
    student.partial_score !== null && student.partial_score !== undefined;
  const scoreValue = hasFinalScore
    ? student.score
    : hasPartialScore
    ? student.partial_score
    : null;
  const title = hasFinalScore
    ? 'Nota final'
    : hasPartialScore
    ? 'Nota parcial'
    : 'Nota';

  return (
    <StatCard
      title={title}
      indicator={
        <Text strong className="!text-2xl !leading-none !text-neutral-900">
          {formatScoreValue(scoreValue)}
        </Text>
      }
    />
  );
};

const CountCard = ({
  icon,
  count,
  label,
  action
}: {
  icon: React.ReactNode;
  count: number;
  label: string;
  action?: React.ReactNode;
}) => (
  <div className="flex items-center gap-3 rounded-lg border border-neutral-200 px-3 py-2">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
      {icon}
    </div>
    <div className="flex min-w-0 flex-1 items-center gap-1.5">
      <Text strong className="!text-neutral-900">
        {count}
      </Text>
      <Text type="secondary" className="!text-sm">
        {label}
      </Text>
    </div>
    {action && <div className="shrink-0 whitespace-nowrap">{action}</div>}
  </div>
);

const PerformanceCard = ({
  academicStatus
}: {
  academicStatus: DisciplineStatusDisplay;
}) => {
  const tone = STATUS_TONES[academicStatus.color] ?? STATUS_TONES.default;
  const StatusIcon = tone.Icon;
  const description = STATUS_DESCRIPTION[academicStatus.status];

  return (
    <div
      className={`flex items-center justify-center gap-3 rounded-lg px-4 py-3 ${tone.card}`}
    >
      <StatusIcon className={`shrink-0 text-3xl ${tone.iconClass}`} />
      <Text strong className={`!text-lg ${tone.titleClass}`}>
        {academicStatus.label}
      </Text>
      {description && (
        <Text type="secondary" className="!text-sm">
          {description}
        </Text>
      )}
    </div>
  );
};

interface StudentDetailsModalProps {
  modalProps: ReturnType<typeof useModal>;
  student?: IStudentShow;
  classroomCode?: string;
  disciplineName?: string;
  handleEditFrequency: (student: IStudentShow) => void;
  canEditFrequency: boolean;
  canAccessForums: boolean;
  canAccessDiscussionThreads: boolean;
}

export const StudentDetailsModal: React.FC<StudentDetailsModalProps> = ({
  student,
  classroomCode,
  disciplineName,
  modalProps,
  handleEditFrequency,
  canEditFrequency,
  canAccessForums,
  canAccessDiscussionThreads
}) => {
  const { id } = useParams();
  const { push, listUrl, showUrl } = useNavigation();

  if (!student) {
    return null;
  }

  const handleNavigateToForum = () => {
    const forumList = showUrl('professor_area/classrooms', id as string);

    push(`${forumList}?tab=forums`);
  };

  const handleNavigateToThreads = () => {
    const threadsList = listUrl('professor_area/discussion_threads');

    push(`${threadsList}?filter[by_user_name]=${student.student_name}`);
  };

  const academicStatus = getDisciplineStatusDisplay(student);
  const attendanceRate = student.attendance_rate;
  const studentEmail = student.course_enrollment.user.email;

  return (
    <Modal
      title={null}
      open={modalProps.visible}
      onCancel={modalProps.close}
      footer={null}
      centered
      width={550}
      styles={{
        body: {
          paddingTop: 8,
          maxHeight: 'calc(100vh - 64px)',
          overflowY: 'auto'
        }
      }}
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-4 pr-8">
          <Avatar size={56} className="bg-primary-500/10 text-primary-600">
            {getInitialsName(student.student_name)}
          </Avatar>
          <div className="min-w-0 flex-1">
            <Title level={4} className="!mb-1 !mt-0 !text-neutral-900">
              {student.student_name}
            </Title>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-neutral-500">
              {studentEmail && (
                <span className="inline-flex items-center gap-1.5">
                  <MailOutlined />
                  <Text type="secondary">Email: {studentEmail}</Text>
                </span>
              )}
              {studentEmail && (
                <span className="hidden h-4 w-px bg-neutral-300 sm:block" />
              )}
              <span className="inline-flex items-center gap-1.5">
                <IdcardOutlined />
                <Text type="secondary">
                  Matrícula: {student.enrollment_registration}
                </Text>
              </span>
            </div>
          </div>
        </div>

        <Divider className="!m-0" />

        <section>
          <SectionTitle icon={<ReadOutlined />}>
            Contexto acadêmico
          </SectionTitle>
          <CourseInfoSection
            student={student}
            classroomCode={classroomCode}
            disciplineName={disciplineName}
          />
        </section>

        <section>
          <SectionTitle icon={<TeamOutlined />}>
            Participação na turma
          </SectionTitle>
          <div className="flex flex-col gap-3">
            {isClassroomForumEnabled() && (
              <CountCard
                icon={<CommentOutlined />}
                count={student.forums_count || 0}
                label="Interações no fórum"
                action={
                  canAccessForums ? (
                    <Button
                      type="link"
                      className="!h-auto !p-0"
                      onClick={handleNavigateToForum}
                    >
                      Ver todas <RightOutlined className="text-[10px]" />
                    </Button>
                  ) : undefined
                }
              />
            )}
            <CountCard
              icon={<QuestionCircleOutlined />}
              count={student.discussion_threads_count || 0}
              label="Dúvidas aos professores"
              action={
                canAccessDiscussionThreads ? (
                  <Button
                    type="link"
                    className="!h-auto !p-0"
                    onClick={handleNavigateToThreads}
                  >
                    Ver dúvidas <RightOutlined className="text-[10px]" />
                  </Button>
                ) : undefined
              }
            />
          </div>
        </section>

        <section>
          <SectionTitle icon={<BarChartOutlined />}>Desempenho</SectionTitle>
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-3 gap-3">
              <StatCard
                title="Frequência"
                indicator={
                  <Progress
                    type="circle"
                    percent={attendanceRate ?? 0}
                    size={56}
                  />
                }
                extra={
                  canEditFrequency ? (
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEditFrequency(student)}
                    >
                      Editar
                    </Button>
                  ) : undefined
                }
              />
              <StatCard
                title="Progresso"
                indicator={
                  <Progress
                    type="circle"
                    percent={student.progress_percentage ?? 0}
                    size={56}
                  />
                }
              />
              <ScoreCard student={student} />
            </div>
            <PerformanceCard academicStatus={academicStatus} />
          </div>
        </section>
      </div>
    </Modal>
  );
};
